import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useBootstrap = (fn) => {
  useEffect(() => {
    fn();
  }, [fn]);
};

const AdminCoupons = () => {
  const { admin, logout, getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", label: "", discount_type: "percent", discount_value: 20, max_uses: "", banner: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [getToken]);

  useBootstrap(load);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, discount_value: parseFloat(form.discount_value) };
      if (!form.max_uses) delete payload.max_uses; else payload.max_uses = parseInt(form.max_uses, 10);
      await axios.post(`${API}/admin/coupons`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Coupon ${form.code.toUpperCase()} created.`);
      setForm({ code: "", label: "", discount_type: "percent", discount_value: 20, max_uses: "", banner: true });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const toggle = async (c, key) => {
    try { await axios.patch(`${API}/admin/coupons/${c.id}`, { [key]: !c[key] }, { headers: { Authorization: `Bearer ${getToken()}` } }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await axios.delete(`${API}/admin/coupons/${c.id}`, { headers: { Authorization: `Bearer ${getToken()}` } }); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <main data-testid="admin-coupons-page" className="min-h-screen bg-[#F7F7F4]">
      <SEO title="Coupons" path="/admin/coupons" noindex />
      <header className="sticky top-0 z-30 bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/admin" className="font-sora text-[22px] font-extrabold tracking-tight text-[#111111]">Vaymilo<span className="text-[#2563EB]">.</span></Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-manrope text-sm text-[#111111]/65">{admin?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope text-sm px-4 py-2 rounded-lg border border-[#E7E7E2] hover:border-[#111111]">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="overline">— Coupons</div>
        <h1 className="mt-3 font-sora text-[40px] md:text-[52px] font-extrabold tracking-tight text-[#111111]">Codes & promos.</h1>
        <Link to="/admin" className="mt-4 inline-block font-manrope text-sm text-[#111111]/65 hover:text-[#111111]">← Back to dashboard</Link>

        <form onSubmit={create} className="mt-10 bg-white border border-[#E7E7E2] rounded-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block font-manrope text-sm mb-2">Code</label>
            <input data-testid="coupon-code" required value={form.code} onChange={(e) => setForm({...form, code: e.target.value})}
              placeholder="LAUNCH" className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm uppercase focus:outline-none focus:border-[#2563EB]" />
          </div>
          <div className="md:col-span-2">
            <label className="block font-manrope text-sm mb-2">Label</label>
            <input value={form.label} onChange={(e) => setForm({...form, label: e.target.value})}
              placeholder="Launch week" className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]" />
          </div>
          <div>
            <label className="block font-manrope text-sm mb-2">Type</label>
            <select value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})}
              className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]">
              <option value="percent">% off</option><option value="flat">$ off</option>
            </select>
          </div>
          <div>
            <label className="block font-manrope text-sm mb-2">Value</label>
            <input type="number" required min="1" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: e.target.value})}
              className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]" />
          </div>
          <div>
            <label className="block font-manrope text-sm mb-2">Max uses</label>
            <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: e.target.value})}
              placeholder="∞" className="w-full bg-white border border-[#E7E7E2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]" />
          </div>
          <div className="md:col-span-5 flex items-center gap-3">
            <label className="inline-flex items-center gap-2 font-manrope text-sm">
              <input type="checkbox" checked={form.banner} onChange={(e) => setForm({...form, banner: e.target.checked})} />
              Show in launch banner
            </label>
          </div>
          <button data-testid="coupon-create-btn" type="submit" className="md:col-span-1 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-sm px-4 py-2.5 rounded-lg">
            <Plus size={14} /> Create
          </button>
        </form>

        <section className="mt-12 bg-white border border-[#E7E7E2] rounded-2xl overflow-hidden">
          <table data-testid="coupons-table" className="w-full font-manrope text-[14px]">
            <thead className="bg-[#F7F7F4]">
              <tr>{["Code","Discount","Used","Banner","Active",""].map(h => (
                <th key={h} className="px-5 py-3 text-left font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/55 border-b border-[#E7E7E2]">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#111111]/50 font-mono text-xs uppercase tracking-widest">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#111111]/60">No coupons yet.</td></tr>}
              {!loading && items.map((c) => (
                <tr key={c.id} className="border-b border-[#E7E7E2] last:border-b-0">
                  <td className="px-5 py-4 font-mono text-[13px] text-[#111111]">{c.code}</td>
                  <td className="px-5 py-4">{c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value} off`}{c.label && <span className="block text-[12px] text-[#111111]/55 mt-0.5">{c.label}</span>}</td>
                  <td className="px-5 py-4 text-[#111111]/70">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                  <td className="px-5 py-4"><button onClick={() => toggle(c, "banner")} className={`px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider ${c.banner ? "bg-[#2563EB] text-white" : "bg-[#F7F7F4] text-[#111111]/55"}`}>{c.banner ? "On" : "Off"}</button></td>
                  <td className="px-5 py-4"><button onClick={() => toggle(c, "active")} className={`px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider ${c.active ? "bg-[#111111] text-white" : "bg-[#F7F7F4] text-[#111111]/55"}`}>{c.active ? "Active" : "Off"}</button></td>
                  <td className="px-5 py-4 text-right"><button onClick={() => remove(c)} className="text-[#111111]/45 hover:text-[#2563EB]"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </main>
  );
};

export default AdminCoupons;
